class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('#predictive-search, #mobile-predictive-search');
    this.isMobile = this.classList.contains('mobile-search-predictive');
    this.isOpen = false;
    this.abortController = null;
    this.searchTerm = '';

    if (!this.input || !this.predictiveSearchResults) return;

    this.input.addEventListener('input', this.debounce(this.onInput.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    
    document.addEventListener('click', this.onClickOutside.bind(this));
    document.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  onInput(event) {
    this.onChange(event);
  }

  onChange(event) {
    const newSearchTerm = this.input.value.trim();

    if (newSearchTerm === '') {
      this.close();
      return;
    }

    if (newSearchTerm === this.searchTerm) {
      return;
    }

    this.searchTerm = newSearchTerm;
    this.getSearchResults(this.searchTerm);
  }

  onFocus() {
    const currentSearchTerm = this.input.value.trim();
    if (currentSearchTerm && currentSearchTerm === this.searchTerm && this.predictiveSearchResults.innerHTML) {
      this.open();
    }
  }

  onFormSubmit() {
    this.close();
  }

  onClickOutside(event) {
    if (!this.contains(event.target) && this.isOpen) {
      this.close();
    }
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
      this.input.focus();
    }
  }

  getSearchResults(searchTerm) {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    this.predictiveSearchResults.classList.add('is-loading');

    const sectionId = this.isMobile ? 'mobile-predictive-search' : 'predictive-search';
    const searchParams = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product',
      'resources[limit]': this.isMobile ? '5' : '6',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,body,variants.sku,variants.barcode',
      section_id: sectionId
    });

    fetch(`${window.Shopify.routes.root}search/suggest?${searchParams.toString()}`, { signal })
      .then(response => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then(text => {
        const resultsMarkup = this.extractSectionContent(text);
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.predictiveSearchResults.classList.remove('is-loading');
        this.open();
        this.setupResultsInteraction();
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          return;
        }
        console.error('Predictive search error:', error);
        this.predictiveSearchResults.classList.remove('is-loading');
        this.close();
      });
  }

  extractSectionContent(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const sectionId = this.isMobile ? '#shopify-section-mobile-predictive-search' : '#shopify-section-predictive-search';
    const section = doc.querySelector(sectionId);
    
    if (!section) {
      return '';
    }

    return section.innerHTML;
  }

  setupResultsInteraction() {
    const links = this.predictiveSearchResults.querySelectorAll('.predictive-search__link');
    const submitButton = this.predictiveSearchResults.querySelector('.predictive-search__view-all');

    links.forEach(link => {
      link.addEventListener('click', () => {
        this.close();
      });
    });

    if (submitButton) {
      submitButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.input.form.submit();
        this.close();
      });
    }
  }

  open() {
    if (this.isOpen) return;
    
    this.predictiveSearchResults.classList.add('is-open');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close() {
    if (!this.isOpen) return;
    
    this.predictiveSearchResults.classList.remove('is-open');
    this.input.setAttribute('aria-expanded', 'false');
    this.isOpen = false;
    this.abortController = null;
  }

  debounce(fn, delay) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}

document.addEventListener('DOMContentLoaded', () => {
  const mobileSearchOverlay = document.getElementById('MobileSearchOverlay');
  const mobileSearchTriggers = document.querySelectorAll('.js-search-header');
  const mobileSearchClose = document.querySelector('.js-mobile-search-close');
  const mobileSearchInput = document.querySelector('.mobile-search-input');

  if (!mobileSearchOverlay) return;

  function openMobileSearch(event) {
    if (window.innerWidth > 768) return;
    
    event.preventDefault();
    mobileSearchOverlay.classList.add('is-active');
    document.body.classList.add('mobile-search-open');
    
    setTimeout(() => {
      if (mobileSearchInput) {
        mobileSearchInput.focus();
      }
    }, 300);
  }

  function closeMobileSearch() {
    mobileSearchOverlay.classList.remove('is-active');
    document.body.classList.remove('mobile-search-open');
    
    if (mobileSearchInput) {
      mobileSearchInput.value = '';
    }
    
    // Only target the predictive search results within the overlay
    const mobilePredictiveSearch = mobileSearchOverlay.querySelector('#mobile-predictive-search');
    if (mobilePredictiveSearch) {
      mobilePredictiveSearch.classList.remove('is-open');
      mobilePredictiveSearch.innerHTML = '';
    }
  }

  mobileSearchTriggers.forEach(trigger => {
    trigger.addEventListener('click', openMobileSearch);
  });

  if (mobileSearchClose) {
    mobileSearchClose.addEventListener('click', closeMobileSearch);
  }

  mobileSearchOverlay.addEventListener('click', (event) => {
    if (event.target === mobileSearchOverlay) {
      closeMobileSearch();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && mobileSearchOverlay.classList.contains('is-active')) {
      closeMobileSearch();
    }
  });
});

